import { useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

// Components
import CalendarLinks from 'components/CalendarLinks';
import OrderReceipt from 'components/OrderReceipt';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';

// Images
import checked_icon from 'images/checked_icon.png';

// Utilities
import { useStorefrontContext } from 'context/StorefrontContext';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Link } from 'react-router-dom';

const AppleWalletEN =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/apple-wallet-e.png';
const AppleWalletFR =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/apple-wallet-f.png';
const GoogleWalletEN =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/google-wallet-e.png';
const GoogleWalletFR =
    'https://frontdoorplus.s3.ca-central-1.amazonaws.com/storefront-images/web/google-wallet-f.png';

export default function PurchaseSuccess() {
    const {
        tranId,
        email,
        event,
        ticketCount,
        appleWalletURL,
        googleWalletURL,
        googleCalendarURL,
        outlookCalendarURL,
        officeCalendarURL,
        calendarURL,
        receiptData,
        handleContinueShopping,
    } = useStorefrontContext();

    const { ctaDescription, ctaButtonText, ctaButtonURL } = event;

    const ctaAvailable = ctaDescription && ctaButtonText && ctaButtonURL;

    // Apple wallet will fail to open if there are more than 10 tickets included
    const showAppleWallet = ticketCount <= 10;

    const { i18n } = useLingui();
    const calendarLinks = {
        google_calendar_url: googleCalendarURL,
        outlook_calendar_url: outlookCalendarURL,
        office_calendar_url: officeCalendarURL,
        calendar_url: calendarURL,
    };

    // Reset scroll position to top after component renders
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="mx-auto w-full max-w-screen-sm">
            <div className="px-2 sm:px-0">
                <div className="mb-16 mt-16 flex w-full items-center justify-center">
                    <img src={checked_icon} alt="" className="h-8 w-8" />
                    <h1 className="ml-2 text-center text-xl font-bold text-foreground sm:text-3xl">
                        <Trans>Purchase Complete!</Trans>
                    </h1>
                </div>

                <Card className="mb-8 border-solid border-border shadow-none">
                    <CardContent className="p-6">
                        <p className="text-center text-foreground">
                            <Trans>We've emailed your tickets and receipt to:</Trans>
                        </p>
                        <p className="mt-4 text-center text-2xl font-bold text-foreground">
                            {email}
                        </p>
                    </CardContent>
                </Card>

                <Card className="mb-8 border-solid border-border shadow-none">
                    <CardContent className="p-6">
                        <p className="text-center text-foreground">
                            <Trans>
                                Save your tickets to your phone wallet for quick access at the
                                event.
                            </Trans>
                        </p>

                        <div className="flex flex-col items-center justify-between px-4 py-8">
                            <div className="flex w-full justify-around">
                                {showAppleWallet && (
                                    <a
                                        href={appleWalletURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="h-10 md:h-12"
                                    >
                                        <img
                                            className="h-10 md:h-12"
                                            src={
                                                i18n.locale === 'fr' ? AppleWalletFR : AppleWalletEN
                                            }
                                            alt={t`Add to Apple Wallet`}
                                        />
                                    </a>
                                )}
                                <a
                                    href={googleWalletURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="h-10 md:h-12"
                                >
                                    <img
                                        className="h-10 md:h-12"
                                        src={i18n.locale === 'fr' ? GoogleWalletFR : GoogleWalletEN}
                                        alt={t`Add to Google Wallet`}
                                    />
                                </a>
                            </div>
                        </div>

                        {showAppleWallet && (
                            <p className="text-center text-xs text-muted-foreground">
                                <Trans>
                                    Apple Wallet is available on supported Apple devices using
                                    Safari.
                                </Trans>
                            </p>
                        )}
                    </CardContent>
                </Card>

                <div className="mb-8">
                    <CalendarLinks links={calendarLinks} eventId={event.eventId} />
                </div>

                <OrderReceipt {...receiptData} tranId={tranId} />

                <Card className="mb-8 border-solid border-border shadow-none">
                    <CardContent className="p-6">
                        <p className="text-center text-foreground">
                            {i18n.locale === 'fr' ? (
                                <>
                                    <Trans>Need Help? Email us at:</Trans>
                                    <a
                                        href={`mailto:hello@frontdoorplus.com?subject=${encodeURIComponent(t`Order F${tranId} Support`)}`}
                                        className="pl-1 text-blue-500 visited:text-blue-500"
                                    >
                                        hello@frontdoorplus.com
                                    </a>
                                </>
                            ) : (
                                <>
                                    <Trans>Need help?</Trans>
                                    <a
                                        href="https://frontdoorplus.com/help/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="pl-1 text-blue-500 visited:text-blue-500"
                                    >
                                        <Trans>Visit Support</Trans>
                                    </a>
                                </>
                            )}
                        </p>
                    </CardContent>
                </Card>

                {ctaAvailable ? (
                    <Card className="mb-8 border-solid border-border shadow-none">
                        <CardContent className="p-6">
                            <p className="mb-6 mx-4 text-center text-xl font-semibold text-foreground">
                                {ctaDescription}
                            </p>
                            <div className="flex flex-col items-center">
                                <Button asChild className="w-[70%] md:w-1/2">
                                    <a
                                        href={ctaButtonURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ExternalLink aria-hidden />
                                        {ctaButtonText}
                                    </a>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="mb-8 border-solid border-border shadow-none">
                        <CardContent className="p-6">
                            <p className="mb-6 mx-4 text-center text-xl font-semibold text-foreground">
                                {t`See more events from` + ' ' + event.hostName}
                            </p>
                            <div className="flex flex-col items-center">
                                <Button asChild className="w-[70%] md:w-1/2">
                                    <Link to={`/host/${event.hostId}`} className="no-underline">
                                        <Trans>View Events</Trans>
                                    </Link>
                                </Button>
                                <Button
                                    variant="outline"
                                    className="mt-4 w-[70%] md:w-1/2"
                                    onClick={handleContinueShopping}
                                >
                                    <Trans>Continue Shopping</Trans>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
